import { useState, useEffect, useCallback } from "react";

// Tạo storage key dựa trên examId và userId
const getStorageKey = (examId) => {
  return `exam_answers_${examId}`;
};

// Lưu đáp án vào localStorage với merge logic
const saveAnswersToStorage = (examId, answers) => {
  try {
    const storageKey = getStorageKey(examId);
    localStorage.setItem(storageKey, JSON.stringify(answers));
  } catch (error) {
    console.error("Error saving answers to localStorage:", error);
  }
};

// Lấy đáp án từ localStorage
const getAnswersFromStorage = (examId) => {
  try {
    const storageKey = getStorageKey(examId);
    const savedAnswers = localStorage.getItem(storageKey);
    if (savedAnswers) {
      return JSON.parse(savedAnswers);
    }
    return null;
  } catch (error) {
    console.error("Error getting answers from localStorage:", error);
    return null;
  }
};

/**
 * Custom hook để quản lý answers cho exam
 * Centralized logic để đồng bộ giữa ExamTestPage và MathRenderer
 */
export const useExamAnswers = (examId, initialAnswers = {}) => {
  const [answers, setAnswers] = useState(initialAnswers);

  // Load answers từ localStorage khi hook được khởi tạo
  useEffect(() => {
    const savedAnswers = getAnswersFromStorage(examId);
    if (savedAnswers) {
      setAnswers(savedAnswers);
    } else if (Object.keys(initialAnswers).length > 0) {
      // Nếu không có saved answers nhưng có initialAnswers, sử dụng initialAnswers
      setAnswers(initialAnswers);
    }
  }, [examId]);

  // Auto-save answers mỗi khi thay đổi
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      saveAnswersToStorage(examId, answers);
    }
  }, [answers, examId]);

  // Function để update answer cho một câu hỏi cụ thể
  const updateAnswer = useCallback((questionId, newAnswer) => {
    setAnswers((prev) => {
      const updated = {
        ...prev,
        [questionId]: newAnswer,
      };
      return updated;
    });
  }, []);

  // Function để update partial answer (cho TLN_M, DS, KT)
  const updatePartialAnswer = useCallback((questionId, partialData) => {
    setAnswers((prev) => {
      const currentAnswer = prev[questionId] || {};
      const updated = {
        ...prev,
        [questionId]: {
          ...currentAnswer,
          ...partialData,
        },
      };
      return updated;
    });
  }, []);

  // Function để toggle item trong array (cho MA type)
  const toggleArrayAnswer = useCallback((questionId, item) => {
    setAnswers((prev) => {
      const currentAnswers = prev[questionId] || [];
      const newAnswers = currentAnswers.includes(item)
        ? currentAnswers.filter((a) => a !== item)
        : [...currentAnswers, item];

      const updated = {
        ...prev,
        [questionId]: newAnswers,
      };
      return updated;
    });
  }, []);

  // Function để clear all answers (cho submission)
  const clearAnswers = useCallback(() => {
    const storageKey = getStorageKey(examId);
    localStorage.removeItem(storageKey);
    setAnswers({});
  }, [examId]);

  // Function để force refresh answers từ storage
  const refreshAnswers = useCallback(() => {
    const savedAnswers = getAnswersFromStorage(examId);
    if (savedAnswers) {
      setAnswers(savedAnswers);
    }
  }, [examId]);

  return {
    answers,
    updateAnswer,
    updatePartialAnswer,
    toggleArrayAnswer,
    clearAnswers,
    refreshAnswers,
    // Utility functions
    getAnswerForQuestion: (questionId) => answers[questionId],
    hasAnswerForQuestion: (questionId) =>
      questionId in answers &&
      answers[questionId] !== null &&
      answers[questionId] !== undefined,
  };
};

export default useExamAnswers;
